// Renders a source cell's cached value with its Excel `number_format` string. The repo has no
// SheetJS, and the KS models use a small vocabulary of formats — thousands with a fixed number of
// decimals, parentheses for negatives, percents, multiples, dates — so this covers that vocabulary
// and falls back to the raw value for anything it does not understand. A format it cannot parse
// never blanks a cell.
//
// Supported: `General`, `0`, `0.0`, `#,##0`, `#,##0.00`, `0%`, `0.0%`, `0.0x`, `_(` / `_)` padding,
// `*` fill, literal text in quotes, section lists `pos;neg;zero;text`, `[Red]` colour tags, and
// date formats built from y / m / d / mmm. Fractions (`# ?/?`), scientific notation and
// conditional sections are rendered raw.

const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const stripNoise = (section) =>
  section
    .replace(/\[[^\]]*\]/g, '') // [Red], [$-409]
    .replace(/_./g, ' ') // _( _) padding → a space
    .replace(/\*./g, '') // * fill
    .replace(/\\(.)/g, '$1'); // escaped literal

const isDateFormat = (section) => {
  const bare = section.replace(/"[^"]*"/g, '');
  return /[ymd]/i.test(bare) && !/[#0]/.test(bare);
};

const excelSerialToDate = (serial) => new Date(EXCEL_EPOCH_MS + Math.round(serial * 86400000));

const formatDate = (date, section) => {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth();
  const d = date.getUTCDate();
  const pad = (n) => String(n).padStart(2, '0');
  // Longest tokens first so `mmmm` is not read as two `mm`.
  return section
    .replace(/"([^"]*)"/g, '$1')
    .replace(/yyyy/gi, String(y))
    .replace(/yy/gi, String(y).slice(-2))
    .replace(/mmmm/g, MONTHS[m])
    .replace(/mmm/g, MONTHS[m])
    .replace(/mm/g, pad(m + 1))
    .replace(/(^|[^a-z])m(?![a-z])/g, `$1${m + 1}`)
    .replace(/dd/g, pad(d))
    .replace(/(^|[^a-z])d(?![a-z])/g, `$1${d}`);
};

// How many decimals, whether to group thousands, whether it is a percent, and the literal text
// around the number, from one section like `#,##0.0,"x"` or `0.0%` or `(#,##0)`.
const parseNumberSection = (section) => {
  const cleaned = stripNoise(section);
  const literals = [];
  // Each literal is swapped for one private-use character (not a digit, so it can never be read
  // as part of the number pattern) and restored afterwards.
  const withoutLiterals = cleaned.replace(/"([^"]*)"/g, (_, lit) => {
    literals.push(lit);
    return String.fromCharCode(0xe100 + literals.length - 1);
  });
  const restoreLiterals = (text) => text.replace(/[\ue100-\ue1ff]/g, (c) => literals[c.charCodeAt(0) - 0xe100]);
  const match = /([#,0]*0[#,0]*)(?:\.([0#]+))?/.exec(withoutLiterals);
  // A section with no digit placeholder is pure text — `"-"` for zero, `"n/a"` — and renders as is.
  if (!match) {
    const text = restoreLiterals(withoutLiterals).trim();
    return text ? { literalOnly: true, text } : null;
  }
  const decimals = match[2] ? match[2].length : 0;
  const grouping = match[1].includes(',');
  const percent = /%/.test(withoutLiterals);
  const [prefixRaw, suffixRaw] = withoutLiterals.split(match[0]);
  const restore = (s) => restoreLiterals(s || '').replace(/[#0,.?]/g, '');
  const prefix = restore(prefixRaw);
  const suffix = restore(suffixRaw).replace('%', percent ? '%' : '');
  const parenthesized = prefix.includes('(') && suffix.includes(')');
  return {
    decimals,
    grouping,
    percent,
    prefix: prefix.replace('(', ''),
    suffix: suffix.replace(')', ''),
    parenthesized,
  };
};

const formatNumberWith = (abs, spec) => {
  const scaled = spec.percent ? abs * 100 : abs;
  return scaled.toLocaleString('en-US', {
    minimumFractionDigits: spec.decimals,
    maximumFractionDigits: spec.decimals,
    useGrouping: spec.grouping,
  });
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

export const formatCell = (value, numberFormat) => {
  if (value === null || value === undefined) return '';
  // Date cells arrive as ISO strings, not serials; a date format still applies to them.
  if (
    typeof value === 'string' &&
    ISO_DATE.test(value) &&
    numberFormat &&
    isDateFormat(String(numberFormat).split(';')[0])
  ) {
    const parsed = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
    if (!Number.isNaN(parsed.getTime())) return formatDate(parsed, stripNoise(String(numberFormat).split(';')[0]));
  }
  if (typeof value !== 'number' || !Number.isFinite(value)) return String(value);
  if (!numberFormat || numberFormat === 'General' || numberFormat === '@') return String(value);

  const sections = String(numberFormat).split(';');

  if (isDateFormat(sections[0])) {
    try {
      return formatDate(excelSerialToDate(value), stripNoise(sections[0]));
    } catch (e) {
      return String(value);
    }
  }

  const positive = parseNumberSection(sections[0]);
  if (!positive || positive.literalOnly) return String(value);

  const negative = sections[1] ? parseNumberSection(sections[1]) : null;
  const zero = sections[2] ? parseNumberSection(sections[2]) : null;

  if (value === 0 && zero) {
    if (zero.literalOnly) return zero.text;
    return `${zero.prefix}${formatNumberWith(0, zero)}${zero.suffix}`.trim();
  }

  if (value < 0) {
    if (negative && negative.literalOnly) return negative.text;
    if (negative) {
      const body = `${negative.prefix}${formatNumberWith(Math.abs(value), negative)}${negative.suffix}`;
      return (negative.parenthesized ? `(${body})` : body).trim();
    }
    return `-${positive.prefix}${formatNumberWith(Math.abs(value), positive)}${positive.suffix}`.trim();
  }

  return `${positive.prefix}${formatNumberWith(value, positive)}${positive.suffix}`.trim();
};
